import type { ReactNode } from 'react';

/**
 * QuietAction — the ONE treatment for a third action that must stay available
 * without competing: a text-weight control below the buttons, never a pill.
 *
 * Owner feedback, twice: "The 3 buttons next to each other feels too much"
 * (PR #28) and "the link gets lost next to all the primary buttons" (PR #39).
 * The shape decided on issue #29 is one primary, one secondary, one quiet —
 * this is the quiet one, and it exists as a component so the treatment cannot
 * drift between the phone and desktop copies of the same row (CLAUDE.md:
 * generalise the existing component, never fork a near-copy).
 *
 * It is a real `<button>`, because every caller acts on the game rather than
 * leaving the app — the one that leaves (CommunityCTA) is an `<a>` and stays
 * one. It also sits in its OWN container at every call site, which is what
 * keeps it out of the adjacent-button cluster R-001 counts.
 */
export function QuietAction({
  children,
  onClick,
  ariaLabel,
}: {
  children: ReactNode;
  onClick: () => void;
  /** Only when the visible label is not the whole story. */
  ariaLabel?: string;
}) {
  return (
    <button
      type="button"
      className="rm-hover"
      aria-label={ariaLabel}
      onClick={onClick}
      style={quietActionStyle}
    >
      {children}
    </button>
  );
}

/**
 * The quiet weight, exported for the rare call site that needs the style on an
 * element it already owns. Font/colour match the community link on the same
 * screens (13px UI, semibold, tertiary ink) so the "quiet" tier reads as one
 * tier rather than three near-misses.
 */
export const quietActionStyle: React.CSSProperties = {
  background: 'none',
  border: 'none',
  padding: 'var(--space-2) var(--space-3)',
  minHeight: 40,
  cursor: 'pointer',
  fontFamily: 'var(--font-ui)',
  fontSize: 13,
  fontWeight: 600,
  letterSpacing: '0.01em',
  color: 'var(--text-tertiary)',
};
